"use client";

import { useState } from "react";

const COLLAB_OPTIONS = [
  "Webinar",
  "Expert Talk",
  "Internships / OJT",
  "Placement",
];

const HEARD_OPTIONS = [
  "Social Media",
  "Google Search",
  "Friend / Colleague",
  "Event / Conference",
  "Email / Newsletter",
  "Other",
];

const SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycby9mN-nuUQ3YzgEyP5Ekthw5tJn8qSLuuMbVz3XDQ4695_edVfR0eFIQb59JvC6NJtQaQ/exec";

const PDF_URL =
  "https://cdn.msu.edu.in/msu-reavamp/pdf/Invitation+to+Corporate.pdf";

const PHOTO_URL =
  "https://cdn.msu.edu.in/msu-reavamp/images/Placement/form-image.jpg";

// ─── CollaborateSection ───────────────────────────────────────────────────────

export default function CollaborateSection() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    company: "",
    location: "",
    collab: [],
    heard: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleCollab = (option) => {
    setForm((prev) => ({
      ...prev,
      collab: prev.collab.includes(option)
        ? prev.collab.filter((o) => o !== option)
        : [...prev.collab, option],
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    const digits = (form.contact || "").replace(/\D/g, "");
    if (!digits) errs.contact = "Contact number is required.";
    else if (digits.length !== 10)
      errs.contact = "Contact number must be 10 digits.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "A valid email is required.";
    if (!form.company.trim()) errs.company = "Company name is required.";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    // Open PDF brochure immediately
    window.open(PDF_URL, "_blank");
    setSubmitted(true);

    // Submit to Google Sheets in background
    // Submit to Google Sheets + Brevo Email
    try {
      const payload = {
        name: form.name,
        contact: form.contact,
        email: form.email,
        company: form.company,
        location: form.location,
        collab: form.collab.join(", "),
        heard: form.heard,
      };

      // Google Sheet
      await fetch(SCRIPT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload),
      });

      // Brevo Email
      await fetch("/api/placement-enquiry", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      console.error("Submission error:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full px-4 py-2.5 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition";

  return (
    <section className="bg-white py-14 page-container flex justify-center">
      <div className="max-w-lg w-full">
        {/* ── LEFT: Heading + Form ── */}
        <div>
          <h2
            className="text-xl md:text-2xl font-bold text-black leading-snug"
            style={{ fontFamily: "saans-bold" }}
          >
            Your Guide to Hiring Industry-Ready Talent
          </h2>
          <p className="text-sm text-black mt-1 mb-6 leading-relaxed">
            Explore how MSU simplifies campus recruitment from registration to
            offer rollout.
          </p>

          <div className="flex flex-col gap-3">
            {/* Name */}
            <div>
              <input
                id="collab-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="Name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`${inputBase} ${errors.name ? "border-red-400 focus:ring-red-300" : ""}`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Contact */}
            <div>
              <input
                id="collab-contact"
                name="contact"
                type="tel"
                autoComplete="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder="Contact Number *"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className={`${inputBase} ${errors.contact ? "border-red-400 focus:ring-red-300" : ""}`}
              />
              {errors.contact && (
                <p className="text-red-500 text-xs mt-1">{errors.contact}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <input
                id="collab-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Email ID *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={`${inputBase} ${errors.email ? "border-red-400 focus:ring-red-300" : ""}`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <input
                id="collab-company"
                name="company"
                type="text"
                autoComplete="organization"
                placeholder="Company Name *"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={`${inputBase} ${errors.company ? "border-red-400 focus:ring-red-300" : ""}`}
              />
              {errors.company && (
                <p className="text-red-500 text-xs mt-1">{errors.company}</p>
              )}
            </div>

            {/* Location */}
            <input
              id="collab-location"
              name="location"
              type="text"
              autoComplete="address-level2"
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={inputBase}
            />

            {/* Collaboration Purpose */}
            <div>
              <p className="text-xs text-black mb-2">
                Purpose of collaboration with Medhavi
              </p>
              <div className="flex flex-wrap gap-2">
                {COLLAB_OPTIONS.map((option) => {
                  const selected = form.collab.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleCollab(option)}
                      className={`text-xs px-3 py-1.5 rounded border font-medium transition select-none ${
                        selected
                          ? "bg-amber-50 border-amber-400 text-amber-800"
                          : "bg-white border-gray-300 text-gray-600 hover:border-amber-300"
                      }`}
                    >
                      {selected && (
                        <span className="mr-1 text-amber-500">✓</span>
                      )}
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Where did you hear */}
            <div className="relative">
              <select
                id="collab-heard"
                name="heard"
                value={form.heard}
                onChange={(e) => setForm({ ...form, heard: e.target.value })}
                className={`${inputBase} appearance-none cursor-pointer pr-9 ${
                  !form.heard ? "text-gray-400" : "text-gray-900"
                }`}
              >
                <option value="" disabled>
                  Where did you hear about us?
                </option>
                {HEARD_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                ▾
              </span>
            </div>

            {/* Submit / Success */}
            {submitted ? (
              <div className="text-center text-green-600 font-medium text-sm py-3 bg-green-50 rounded">
                ✓ Thank you! Your enquiry has been submitted.
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full bg-amber-400 hover:bg-amber-500 active:scale-[0.98] text-black font-bold text-xs tracking-widest py-3 rounded flex items-center justify-center gap-2 transition ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                {!isSubmitting && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="11"
                    viewBox="0 0 13 13"
                    fill="none"
                  >
                    <path
                      d="M9.0467 10.5311L5.71326e-06 1.48437L1.48612 -0.00174037L10.5328 9.04495L10.5328 0.722738L12.6319 0.741314V12.6302H0.743061L0.724484 10.5311L9.0467 10.5311Z"
                      fill="black"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT: Photo ── */}
        {/* <div className="overflow-hidden">
            <img
              src={PHOTO_URL}
              alt="Medhavi Skills University Team"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "https://cdn.msu.edu.in/msu-reavamp/images/Placement/form-image.jpg";
              }}
            />
          </div> */}
      </div>
    </section>
  );
}
